import { useCallback, useLayoutEffect, useRef } from 'react';

/** Wer weniger als eine Bubble-Höhe vom Ende entfernt steht, liest das Ende — nicht die Historie. */
const NEAR_BOTTOM_PX = 64;

/**
 * Hält den Verlauf am **neuesten** Eintrag.
 *
 * Der Container startete bisher oben, also beim ältesten Eintrag — in `features/inbox/` gab es
 * weder `scrollIntoView` noch `scrollTop`. Ein Chat wird aber von hinten gelesen.
 *
 * Zwei Momente sind geregelt, und einer ausdrücklich nicht:
 * - **Threadwechsel** springt immer ans Ende, egal wo der vorige Thread stand.
 * - **Neue Nachricht** springt nur ans Ende, wenn der Leser dort ohnehin stand — oder wenn
 *   `stickNow()` es angesagt hat, was der Composer beim Senden tut.
 * - Ein Leser, der bewusst nach oben gescrollt hat, wird von den 30-Sekunden-Refetches **nicht**
 *   nach unten gerissen. Das wäre die Überkorrektur.
 *
 * `useLayoutEffect` statt `useEffect`: der Sprung passiert vor dem Zeichnen, sonst blitzt der
 * älteste Eintrag kurz auf.
 */
export function useStickToNewest(threadId: string, messageCount: number) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const lastThread = useRef<string | null>(null);
  const lastCount = useRef(0);
  // Zuletzt gemessene Höhen: ein Scroll-Ereignis bei geänderten Höhen kam vom Layout (Anker-
  // Korrektur des Browsers), nicht vom Leser, und darf das Festhalten nicht lösen.
  const size = useRef({ client: 0, scroll: 0 });
  const measure = (el: HTMLDivElement) => {
    size.current = { client: el.clientHeight, scroll: el.scrollHeight };
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const threadChanged = lastThread.current !== threadId;
    const grew = messageCount > lastCount.current;
    lastThread.current = threadId;
    lastCount.current = messageCount;

    if (threadChanged) stick.current = true;
    else if (!grew || !stick.current) return;
    el.scrollTop = el.scrollHeight;
    measure(el);
  }, [threadId, messageCount]);

  // Schrumpft der Verlauf (das Kontext-Band darüber lädt nach) oder wächst sein Inhalt ohne neue
  // Nachricht (Schriften laden, Umbruch), bleibt am Ende, wer dort las — sonst verschwände die
  // neueste Nachricht halb unter dem Schreibfeld.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
      measure(el);
    });
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Ohne frühere Messung gibt es nichts zu vergleichen; dann zählt das Ereignis als Lesen.
    const layoutMoved =
      size.current.client > 0 &&
      (el.clientHeight !== size.current.client || el.scrollHeight !== size.current.scroll);
    measure(el);
    if (layoutMoved) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  /** Nach dem Absenden gehört die eigene Antwort ins Bild, auch wenn oben gelesen wurde. */
  const stickNow = useCallback(() => {
    stick.current = true;
  }, []);

  return { ref, contentRef, onScroll, stickNow };
}
